import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  onSnapshot, 
  deleteDoc, 
  doc, 
  updateDoc,
  getFirestore,
  getDoc
} from 'firebase/firestore';
import { auth } from './firebaseConfig';
import { 
  CreditCard, 
  Plus, 
  Edit, 
  Trash2, 
  Save,
  Building
} from 'lucide-react';
import './BankaTanitim.css';

const BankaTanitim = () => {
  const [bankalar, setBankalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBanka, setEditingBanka] = useState(null);
  const [newBanka, setNewBanka] = useState({
    bankaAdi: '',
    adSoyad: '',
    iban: '',
    aciklama: ''
  });

  const db = getFirestore();
  const currentUser = auth.currentUser;

  // Banka listesini getir
  useEffect(() => {
    if (!currentUser?.email) {
      setLoading(false);
      return;
    }

    const bankalarRef = collection(db, `users/${currentUser.email}/bankalar`);
    
    const unsubscribe = onSnapshot(bankalarRef, (snapshot) => {
      const bankaData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setBankalar(bankaData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser?.email, db]);

  // Yeni banka ekleme modal'ını aç
  const openAddModal = () => {
    setNewBanka({
      bankaAdi: '',
      adSoyad: '',
      iban: '',
      aciklama: ''
    });
    setShowAddModal(true);
  };

  // Yeni banka ekleme modal'ını kapat
  const closeAddModal = () => {
    setShowAddModal(false);
    setNewBanka({
      bankaAdi: '',
      adSoyad: '',
      iban: '',
      aciklama: ''
    });
  };

  // IBAN validasyonu
  const validateIban = (iban) => {
    if (!iban) return true; // IBAN opsiyonel
    const ibanRegex = /^TR\d{2}\s?(\d{4}\s?\d{4}\s?\d{4}\s?\d{4}\s?\d{4}\s?\d{2}|\d{24})$/;
    return ibanRegex.test(iban.replace(/\s/g, ''));
  };

  // Input değişikliklerini handle et
  const handleInputChange = (field, value) => {
    setNewBanka(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Yeni banka ekle
  const handleAddBanka = async () => {
    if (!newBanka.bankaAdi.trim()) {
      alert('Banka adı zorunludur!');
      return;
    }

    if (newBanka.iban && !validateIban(newBanka.iban)) {
      alert('Geçerli bir IBAN numarası giriniz! (TR ile başlamalı ve 26 haneli olmalı)');
      return;
    }

    try {
      const bankalarRef = collection(db, `users/${currentUser.email}/bankalar`);
      await addDoc(bankalarRef, {
        ...newBanka,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      // setupProgress'i güncelle
      const userDocRef = doc(db, 'users', currentUser.email);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const currentSetupProgress = userDoc.data().setupProgress || {};
        await updateDoc(userDocRef, {
          'setupProgress': {
            ...currentSetupProgress,
            banks: true
          }
        });
      }

      closeAddModal();
    } catch (error) {
      alert('Banka eklenirken bir hata oluştu!');
    }
  };

  // Düzenleme modal'ını aç
  const openEditModal = (banka) => {
    setEditingBanka({ ...banka });
    setShowEditModal(true);
  };

  // Düzenleme modal'ını kapat
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingBanka(null);
  };

  // Düzenleme input değişikliklerini handle et
  const handleEditInputChange = (field, value) => {
    setEditingBanka(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Banka güncelle
  const handleUpdateBanka = async () => {
    if (!editingBanka.bankaAdi.trim()) {
      alert('Banka adı zorunludur!');
      return;
    }

    if (editingBanka.iban && !validateIban(editingBanka.iban)) {
      alert('Geçerli bir IBAN numarası giriniz! (TR ile başlamalı ve 26 haneli olmalı)');
      return;
    }

    try {
      const bankaDocRef = doc(db, `users/${currentUser.email}/bankalar`, editingBanka.id);
      await updateDoc(bankaDocRef, {
        ...editingBanka,
        updatedAt: new Date().toISOString()
      });

      closeEditModal();
    } catch (error) {
      alert('Banka güncellenirken bir hata oluştu!');
    }
  };

  // Banka sil
  const handleDeleteBanka = async (bankaId) => {
    if (!window.confirm('Bu banka hesabını silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const bankaDocRef = doc(db, `users/${currentUser.email}/bankalar`, bankaId);
      await deleteDoc(bankaDocRef);
    } catch (error) {
      alert('Banka silinirken bir hata oluştu!');
    }
  };

  if (loading) {
    return (
      <div className="banka-tanitim-loading">
        <div className="loading-spinner"></div>
        <p>Banka hesapları yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="banka-tanitim-container">
      <div className="banka-tanitim-header">
        <h2>
          <Building className="header-icon" />
          Banka Tanıtım
        </h2>
        <button 
          className="add-banka-btn"
          onClick={openAddModal}
        >
          <Plus size={16} />
          Yeni Banka Hesabı Ekle
        </button>
      </div>

      <div className="banka-list-container">
        {bankalar.length === 0 ? (
          <div className="no-banka">
            <Building size={48} />
            <h3>Henüz banka hesabı eklenmemiş</h3>
            <p>İlk banka hesabınızı eklemek için yukarıdaki butona tıklayın.</p>
          </div>
        ) : (
          <div className="banka-grid">
            {bankalar.map((banka) => (
              <div key={banka.id} className="banka-card">
                <div className="banka-card-header">
                  <div className="banka-info">
                    <h3>
                      {banka.bankaAdi}
                      {banka.adSoyad && <span className="ad-soyad"> - {banka.adSoyad}</span>}
                    </h3>
                    {banka.iban && (
                      <p className="banka-iban">
                        <strong>IBAN:</strong> {banka.iban}
                      </p>
                    )}
                    {banka.aciklama && (
                      <p className="banka-aciklama">
                        <strong>Açıklama:</strong> {banka.aciklama}
                      </p>
                    )}
                  </div>
                  <div className="banka-actions">
                    <button 
                      className="edit-btn"
                      onClick={() => openEditModal(banka)}
                      title="Düzenle"
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDeleteBanka(banka.id)}
                      title="Sil"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Yeni Banka Ekleme Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Yeni Banka Hesabı Ekle</h3>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <input
                  type="text"
                  value={newBanka.bankaAdi}
                  onChange={(e) => handleInputChange('bankaAdi', e.target.value)}
                  placeholder={newBanka.bankaAdi ? "" : "Banka Adı *"}
                />
              </div>
              
              <div className="form-group">
                <input
                  type="text"
                  value={newBanka.adSoyad}
                  onChange={(e) => handleInputChange('adSoyad', e.target.value)}
                  placeholder={newBanka.adSoyad ? "" : "Ad Soyad"}
                />
              </div>
              
              <div className="form-group">
                <input
                  type="text"
                  value={newBanka.iban}
                  onChange={(e) => handleInputChange('iban', e.target.value)}
                  placeholder={newBanka.iban ? "" : "IBAN"}
                  maxLength="34"
                />
              </div>
              
              <div className="form-group">
                <textarea
                  value={newBanka.aciklama}
                  onChange={(e) => handleInputChange('aciklama', e.target.value)}
                  placeholder={newBanka.aciklama ? "" : "Açıklama"}
                  rows="3"
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="cancel-btn" onClick={closeAddModal}>
                İptal
              </button>
              <button className="save-btn" onClick={handleAddBanka}>
                <Save size={16} />
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Banka Düzenleme Modal */}
      {showEditModal && editingBanka && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Banka Hesabını Düzenle</h3>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <input
                  type="text"
                  value={editingBanka.bankaAdi}
                  onChange={(e) => handleEditInputChange('bankaAdi', e.target.value)}
                  placeholder={editingBanka.bankaAdi ? "" : "Banka Adı *"}
                />
              </div>
              
              <div className="form-group">
                <input
                  type="text"
                  value={editingBanka.adSoyad || ''}
                  onChange={(e) => handleEditInputChange('adSoyad', e.target.value)}
                  placeholder={editingBanka.adSoyad ? "" : "Ad Soyad"}
                />
              </div>
              
              <div className="form-group">
                <input
                  type="text"
                  value={editingBanka.iban || ''}
                  onChange={(e) => handleEditInputChange('iban', e.target.value)}
                  placeholder={editingBanka.iban ? "" : "IBAN"}
                  maxLength="34"
                />
              </div>
              
              <div className="form-group">
                <textarea
                  value={editingBanka.aciklama || ''}
                  onChange={(e) => handleEditInputChange('aciklama', e.target.value)}
                  placeholder={editingBanka.aciklama ? "" : "Açıklama"}
                  rows="3"
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="cancel-btn" onClick={closeEditModal}>
                İptal
              </button>
              <button className="save-btn" onClick={handleUpdateBanka}>
                <Save size={16} />
                Güncelle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankaTanitim; 